import { ATexts } from '../../services/ATexts';
import { ApiService } from '../../api/services/ApiService';
import { CommonTexts } from '../../services/CommonTexts';
import { EntitiesIcons } from './EntitiesIcons';
import type { FormOption } from '../../components/forms/forms.types';
import type { OrganizationBody, PersonBody, PositionBody } from '../../api/types/entities.types';

/** A person's names — a list row, a GET or a contact carries them. */
type PersonNames = Pick<PersonBody, 'person_id' | 'titles' | 'given_name' | 'family_name' | 'nickname'>;

/**
 * The texts only the Entities module shows, and what is built from a record — names for headlines, cards and choices,
 * and the choices of the edit dialogs. One place, so a person reads the same everywhere.
 */
class EntitiesTextsClass extends ATexts {
    public readonly AccountCreate = 'Konto erstellen';
    public readonly TagRequired = 'Bitte wähle mindestens einen Tag aus.';
    public readonly Tags = 'Tags';
    public readonly TagsEdit = 'Tags bearbeiten';
    public readonly Contacts = 'Ansprechpartner';
    public readonly ContactsEdit = 'Ansprechpartner bearbeiten';
    public readonly EmailRequired = 'E-Mail-Adresse erforderlich';
    public readonly IsActive = 'Aktiv?';
    public readonly IsUsed = 'Verwendet?';
    public readonly MakeUser = 'Zum Benutzer machen?';
    public readonly NoContacts = 'Keine Ansprechpartner zugeordnet.';
    public readonly NoPositions = 'Keine Positionen vorhanden.';
    public readonly NoUserAccount = 'Kein Benutzerkonto verknüpft.';
    public readonly NoUserAccountNoEmail = 'Kein Benutzerkonto verknüpft (ohne E-Mail-Adresse nicht möglich).';
    public readonly Organization = 'Firma';
    public readonly OrganizationCreate = 'Firma anlegen';
    public readonly OrganizationDelete = 'Firma löschen';
    public readonly OrganizationEdit = 'Firma bearbeiten';
    public readonly OrganizationFallback = 'Firma #{id}';
    public readonly OrganizationId = 'Firmen-ID';
    public readonly Organizations = 'Firmen';
    public readonly OrganizationsEdit = 'Firmen bearbeiten';
    public readonly OrganizationsEmpty = 'Es gibt noch keine Firmen.';
    public readonly PasswordSendInitial = 'Initiales Passwort per E-Mail senden';
    public readonly PersonCreate = 'Person anlegen';
    public readonly PersonDelete = 'Person löschen';
    public readonly PersonFallback = 'Person #{id}';
    public readonly PersonIsUser = 'Person gehört zu einem Benutzer';
    public readonly PersonsEmpty = 'Es gibt noch keine Personen.';
    public readonly PositionCreate = 'Position anlegen';
    public readonly PositionDelete = 'Position löschen';
    public readonly PositionEdit = 'Position bearbeiten';
    public readonly PositionFallback = 'Position #{id}';
    public readonly PositionId = 'Positions-ID';
    public readonly PositionUsed = 'Position wird verwendet';
    public readonly PositionsEmpty = 'Es gibt noch keine Positionen.';
    public readonly PositionsSort = 'Positionen sortieren';
    public readonly ToUserAccount = 'Zum Benutzerkonto';
    public readonly UserAccount = 'Benutzerkonto';
    public readonly UserAccountCreate = 'Benutzerkonto anlegen';
    public readonly UserAccountCreateHint = 'Für diese Person wird ein neues Benutzerkonto mit aktivem Status und zufälliger Farbe erstellt.';
    public readonly ContactPending = 'Bitte übernimm oder verwirf die neue Zuordnung.';
    public readonly PositionSortFailed = 'Die Sortierung konnte nicht gespeichert werden.';

    /** The API's limit per page: the choices hold every record up to it. */
    private readonly OptionsPerPage = 500;

    public getOrganizationName(organization: Pick<OrganizationBody, 'organization_id' | 'organization'>): string {
        return organization.organization || this.format(this.OrganizationFallback, { id: organization.organization_id });
    }

    /** Titles, given and family name — the nickname without both, the id without any. */
    public getPersonName(person: PersonNames): string {
        const FullName = [person.titles, person.given_name, person.family_name].filter(Boolean).join(' ');

        return FullName || person.nickname || this.format(this.PersonFallback, { id: person.person_id });
    }

    /** The headline of a person's card: titles and names, the nickname without them. */
    public getPersonTitle(person: Pick<PersonBody, 'titles' | 'given_name' | 'family_name' | 'nickname'>): string {
        return [person.titles, person.given_name, person.family_name].filter(Boolean).join(' ') || (person.nickname ?? CommonTexts.Person);
    }

    public getPositionName(position: Pick<PositionBody, 'position_id' | 'position'>): string {
        return position.position || this.format(this.PositionFallback, { id: position.position_id });
    }

    public async listOrganizationOptions(): Promise<FormOption[]> {
        const Result = await ApiService.entities.listOrganizations({ per_page: this.OptionsPerPage, sort: 'organization' });

        return (Result.body?.organizations ?? []).map((organization: OrganizationBody): FormOption => ({
            value: organization.organization_id,
            label: this.getOrganizationName(organization),
            icon: EntitiesIcons.Organization,
        }));
    }

    public async listPersonOptions(): Promise<FormOption[]> {
        const Result = await ApiService.entities.listPersons({ per_page: this.OptionsPerPage, sort: 'family_name' });

        return (Result.body?.persons ?? []).map((person: PersonNames): FormOption => ({ value: person.person_id, label: this.getPersonName(person), person }));
    }

    public async listPositionOptions(): Promise<FormOption[]> {
        const Result = await ApiService.entities.listPositions({ per_page: this.OptionsPerPage, sort: 'sort' });

        return (Result.body?.positions ?? []).map((position: PositionBody): FormOption => ({
            value: position.position_id,
            label: position.position,
            icon: EntitiesIcons.Position,
        }));
    }
}

export const EntitiesTexts = new EntitiesTextsClass();
